import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';

import { AuthFieldValidationIconsComponent } from 'ish-shared/components/login/auth-modals/auth-field-validation-icons.component';
import { PresidioDemoErpAccount } from 'ish-shared/components/login/auth-modals/demo-erp-modal.component';
import { PresidioAuthModalService } from 'ish-shared/components/login/auth-modals/presidio-auth-modal.service';
import { RequestAccessPageBaseComponent } from 'ish-shared/components/login/auth-modals/request-access-form.base';
import { PresidioPasswordInputComponent } from 'ish-shared/forms/components/presidio-password-input/presidio-password-input.component';
import { PresidioPasswordRequirementsComponent } from 'ish-shared/forms/components/presidio-password-requirements/presidio-password-requirements.component';

/**
 * Presidio — Request access as NgbModal overlay on Sign-in.
 * DEMO-ONLY: click ERP customer number to pick admin / CSR / not-found fixtures.
 */
@Component({
  selector: 'ish-request-access-modal',
  templateUrl: './request-access-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    AuthFieldValidationIconsComponent,
    PresidioPasswordInputComponent,
    PresidioPasswordRequirementsComponent,
  ],
})
export class RequestAccessModalComponent extends RequestAccessPageBaseComponent {
  private activeModal = inject(NgbActiveModal);
  private authModals = inject(PresidioAuthModalService);

  private demoErpPickerOpen = false;
  /** Ignore ERP-field clicks briefly after the picker closes (nested modal restores focus). */
  private demoErpPickerSuppressUntil = 0;

  hide(): void {
    this.activeModal.dismiss();
  }

  /** Close Request access and open Forgot password (duplicate-email recovery). */
  openForgotPassword(): void {
    // Service dismisses the open Request access modal, then opens Forgot password.
    this.authModals.openForgot();
  }

  /** DEMO-ONLY — open ERP account-type picker (same pattern as Sign-in email click). */
  onErpFieldActivate(event: Event): void {
    if (SSR || this.demoErpPickerOpen || this.step !== 'erp' || this.form.controls.erpCustomerNo.disabled) {
      return;
    }
    if (Date.now() < this.demoErpPickerSuppressUntil) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target || !this.isErpFieldTarget(target)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.demoErpPickerOpen = true;
    this.cdRef.markForCheck();

    void this.authModals.openDemoErpAccounts().then(account => {
      this.demoErpPickerOpen = false;
      // Nested NgbModal close restores focus to the ERP input and can re-fire click/focus.
      this.demoErpPickerSuppressUntil = Date.now() + 400;
      if (account) {
        this.fillDemoErp(account);
      }
      this.cdRef.markForCheck();
    });
  }

  private isErpFieldTarget(target: HTMLElement): boolean {
    if (target.closest('#erpCustomerNo')) {
      return true;
    }
    const label = target.closest('label[for="erpCustomerNo"]');
    return !!label;
  }

  fillDemoErp(account: PresidioDemoErpAccount): void {
    // valueChanges clears a prior failed-ERP lock when the number differs.
    this.form.controls.erpCustomerNo.setValue(account.erpCustomerNo);
    this.form.controls.erpCustomerNo.markAsDirty();
    this.form.controls.erpCustomerNo.markAsTouched();
    this.cdRef.markForCheck();
  }
}
